import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ImagePickerButton from '../shared/ImagePickerButton';
import { NOTE_IMAGE_MAX_UPLOAD_SIZE, NOTE_IMAGE_ALLOWED_TYPES, NOTE_IMAGE_ACCEPT_STRING } from './imageConstants';

function EditNoteDialog({ open, handleClose, note, maintenanceId, setMaintenance }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [showOnInvoice, setShowOnInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  useEffect(() => {
    if (note) {
      setText(note.text || '');
      setNoteType(note.note_type || 'internal');
      setShowOnInvoice(note.show_on_invoice || false);
      setExistingImages(note.images || []);
      setNewImages([]);
      setDeleteConfirm(false);
      setError(null);
      setImageToDelete(null);
    }
  }, [note]);

  const handleImageSelect = async (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      if (!NOTE_IMAGE_ALLOWED_TYPES.includes(file.type)) {
        setError(t('Invalid file type. Allowed: JPEG, PNG, WebP, GIF'));
        return false;
      }
      if (file.size > NOTE_IMAGE_MAX_UPLOAD_SIZE) {
        setError(t('File too large (max {{maxMB}}MB)', { maxMB: NOTE_IMAGE_MAX_UPLOAD_SIZE / 1024 / 1024 }));
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Upload images immediately
    setUploadingImages(true);
    setError(null);

    try {
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append('images', file);
      });

      const response = await axios.post(
        `/api/maintenances/${maintenanceId}/notes/${note.id}/images/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMaintenance(response.data);

      // Update existing images from response
      const updatedNote = response.data.notes?.find((n) => n.id === note.id);
      if (updatedNote) {
        setExistingImages(updatedNote.images || []);
      }

      if (response.data.image_warnings) {
        setError(response.data.image_warnings.join(', '));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('Error uploading images'));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (imageToDelete !== imageId) {
      setImageToDelete(imageId);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.delete(
        `/api/maintenances/${maintenanceId}/notes/${note.id}/images/${imageId}/`
      );
      setMaintenance(response.data);

      // Update existing images from response
      const updatedNote = response.data.notes?.find((n) => n.id === note.id);
      if (updatedNote) {
        setExistingImages(updatedNote.images || []);
      } else {
        setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
      }
      setImageToDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || t('Error deleting image'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Allow empty text if there are images
    const hasContent = text.trim() || existingImages.length > 0;
    if (!hasContent) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.patch(`/api/maintenances/${maintenanceId}/update_note/${note.id}/`, {
        text: text.trim(),
        note_type: noteType,
        show_on_invoice: noteType === 'customer' ? showOnInvoice : false,
      });
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('Error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.delete(`/api/maintenances/${maintenanceId}/delete_note/${note.id}/`);
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('Error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setNoteType(newType);
    }
  };

  const handleCancel = () => {
    setDeleteConfirm(false);
    setError(null);
    setImageToDelete(null);
    handleClose();
  };

  if (!open) return null;

  const canSubmit = text.trim() || existingImages.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 600,
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {t('Edit note')}
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mb: 1.5,
            }}
          >
            {t('Note type')}
          </Typography>
          <ToggleButtonGroup
            value={noteType}
            exclusive
            onChange={handleTypeChange}
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                py: 1.5,
                borderRadius: '8px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                textTransform: 'none',
                '&.Mui-selected': {
                  fontWeight: 600,
                },
              },
            }}
          >
            <ToggleButton
              value="internal"
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(100, 116, 139, 0.1)',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'rgba(100, 116, 139, 0.15)',
                  },
                },
              }}
            >
              <LockIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('Internal note')}
            </ToggleButton>
            <ToggleButton
              value="customer"
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(49, 130, 206, 0.1)',
                  color: 'secondary.main',
                  '&:hover': {
                    bgcolor: 'rgba(49, 130, 206, 0.15)',
                  },
                },
              }}
            >
              <PublicIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('Customer note')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {noteType === 'customer' && (
          <Alert
            severity="info"
            icon={<PublicIcon />}
            sx={{
              mb: 2,
              borderRadius: '8px',
              '& .MuiAlert-message': {
                fontSize: '13px',
              },
            }}
          >
            {t('Customer note warning')}
          </Alert>
        )}

        {noteType === 'internal' && (
          <Alert
            severity="warning"
            icon={<LockIcon />}
            sx={{
              mb: 2,
              borderRadius: '8px',
              bgcolor: 'rgba(100, 116, 139, 0.08)',
              color: 'text.secondary',
              '& .MuiAlert-icon': {
                color: 'text.secondary',
              },
              '& .MuiAlert-message': {
                fontSize: '13px',
              },
            }}
          >
            {t('Internal note info')}
          </Alert>
        )}

        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          placeholder={
            noteType === 'customer'
              ? t('Write a message for the customer...')
              : t('Write an internal note...')
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
        />

        {noteType === 'customer' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnInvoice}
                onChange={(e) => setShowOnInvoice(e.target.checked)}
                size="small"
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {t('Show on invoice')}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('Show on invoice help')}
                </Typography>
              </Box>
            }
            sx={{ mt: 1.5, ml: 0 }}
          />
        )}

        {/* Image Management Section */}
        <Box sx={{ mt: 2 }}>
          <ImagePickerButton
            onChange={handleImageSelect}
            accept={NOTE_IMAGE_ACCEPT_STRING}
            multiple
            disabled={uploadingImages}
            buttonLabel={uploadingImages ? t('Uploading...') : t('Add images')}
            buttonProps={{
              variant: 'outlined',
              startIcon: uploadingImages ? <CircularProgress size={16} /> : <AttachFileIcon />,
              size: 'small',
              sx: {
                borderRadius: '8px',
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                textTransform: 'none',
              },
            }}
          />

          {existingImages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: 'text.secondary',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ImageIcon sx={{ fontSize: 16 }} />
                {existingImages.length} {existingImages.length === 1 ? t('image') : t('images')}
              </Typography>
              <ImageList
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
                cols={4}
                rowHeight={80}
                gap={8}
              >
                {existingImages.map((img) => (
                  <ImageListItem
                    key={img.id}
                    sx={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: imageToDelete === img.id ? 'error.main' : 'divider',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={img.thumbnail_url || img.url}
                      alt={img.original_filename}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(img.url, '_blank')}
                    />
                    <ImageListItemBar
                      sx={{
                        background:
                          imageToDelete === img.id
                            ? 'rgba(239, 68, 68, 0.9)'
                            : 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                        '& .MuiImageListItemBar-titleWrap': {
                          padding: '4px',
                        },
                      }}
                      position="bottom"
                      title={imageToDelete === img.id ? t('Click to confirm') : ''}
                      actionIcon={
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(img.id);
                          }}
                          disabled={loading}
                          sx={{
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.2)',
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      }
                      actionPosition="right"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ mt: 1, fontSize: '13px' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          disabled={loading}
          sx={{
            borderRadius: '8px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {deleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCancel}
            sx={{
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            sx={{
              bgcolor: noteType === 'customer' ? 'secondary.main' : 'grey.700',
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
              '&:hover': {
                bgcolor: noteType === 'customer' ? 'secondary.dark' : 'grey.800',
              },
            }}
          >
            {loading ? <span>{t('Saving...')}</span> : <span>{t('Save')}</span>}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditNoteDialog;
